import React, { useState, useEffect } from "react";
import API from "../../api/API";
import Authentication from "../../api/Authentication/Authentication";
import { FaSort, FaSortUp, FaSortDown, FaEdit, FaTrash, FaSave, FaPlus } from "react-icons/fa";

const AdminUsers = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 10; // Pagination limit
  

  // CRUD
  const [editingUser, setEditingUser] = useState(null);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [newUser, setNewUser] = useState({ username: "", email: "", password: "", isAdmin: false, enabled: false });
  const [statusMessage, setStatusMessage] = useState("");

  // Handle inline editing
  const handleEdit = (user) => {
    setEditingUser({ ...user });
  };

  const handleSave = () => {
    onUpdateUser(editingUser);
    setEditingUser(null);
  };

  // Handle user deletion
  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this user?")) {
      onDeleteUser(id);
    }
  };

  // Handle new user addition
  const handleAddUser = async () => {
    if (!newUser.username.trim()) return setStatusMessage("Username cannot be empty.");
    if (!newUser.password.trim()) return setStatusMessage("Password cannot be empty.");
    await onAddUser(newUser);
    setShowAddUserModal(false);
    setNewUser({ username: "", email: "", password: "", isAdmin: false, enabled: false });
  };

  const updateUsers = () => {
    API.getAllUsers(currentUser._id)
    .then((response) => {
      if (response.data.success) {
        setUsers(response.data.info);
      } else {
        setError("Failed to load users.");
      }
      setLoading(false);
    })
    .catch((err) => {
      console.error("Error fetching users:", err);
      setError("An error occurred while fetching users.");
      setLoading(false);
    });
  }

  const onAddUser = async (user) => {    
    setLoading(true);
    await API.createNewUser(currentUser._id, user)
      .then((response) => {
        if (response.data.success) {
          setStatusMessage("User created successfully.");
        } else {
          setStatusMessage(response.data.info || "Failed to update username.");
        }
      })
      .catch(() => setStatusMessage("An error occurred while updating username."));
    updateUsers();
    setLoading(false);
  }

  const onUpdateUser = async (user) => {
    setLoading(true);
    await API.updateUser(currentUser._id, user)
      .then((response) => {
        if (response.data.success) {
          setStatusMessage("User created successfully.");
        } else {
          setStatusMessage(response.data.info || "Failed to update username.");
        }
      })
      .catch(() => setStatusMessage("An error occurred while updating username."));
    updateUsers();
    setLoading(false);
  }

  const onDeleteUser = async (id) => {    
    setLoading(true);
    await API.deleteAccount(id)
      .then((response) => {
        if (response.data.success) {
          setStatusMessage("User deleted successfully.");
        } else {
          setStatusMessage(response.data.info || "Failed to delete user.");
        }
      })
      .catch(() => setStatusMessage("An error occurred while deleting user."));
    updateUsers();
    setLoading(false);
  }

  // Fetch the current admin user on mount.
  useEffect(() => {
    const user = Authentication.getCurrentUser();
    setCurrentUser(user);
  }, []);

  // When currentUser is available, fetch all users.
  useEffect(() => {
    if (currentUser) {
      API.getAllUsers(currentUser._id)
        .then((response) => {
          if (response.data.success) {
            setUsers(response.data.info);
          } else {
            setError("Failed to load users.");
          }
          setLoading(false);
        })
        .catch((err) => {
          console.error("Error fetching users:", err);
          setError("An error occurred while fetching users.");
          setLoading(false);
        });
    }
  }, [currentUser]);

  // Function to sort users by column
  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const sortedUsers = [...users].sort((a, b) => {
    if (sortConfig.key) {
      const aValue = a[sortConfig.key] || "";
      const bValue = b[sortConfig.key] || "";
      console.log(aValue)
      console.log(bValue)
      return sortConfig.direction === "asc"
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }
    return 0;
  });

  // Filter users based on search query
  const filteredUsers = sortedUsers.filter(
    (user) =>
      user.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user._id.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Pagination Logic
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  if (error) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-gray-100">
        <p className="text-xl text-red-500">{error}</p>
      </div>
    );
  }

  return (
    <div className="ma-h-screen bg-gray-100 p-6">
      <div className="max-w-6xl mx-auto bg-white rounded-lg shadow-lg p-6">
        <h1 className="text-3xl font-bold mb-6 text-center">User Management</h1>

        {/* Search Bar */}
        <div className="mb-4 flex justify-end">
          <input
            type="text"
            placeholder="Search users..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full max-w-sm p-2 border border-gray-300 rounded focus:outline-none focus:ring focus:border-blue-300"
          />
        </div>

        <div className="bg-white shadow-md rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">User Management</h2>
            <button className="bg-green-500 text-white px-4 py-2 rounded" onClick={() => setShowAddUserModal(true)}>
              <FaPlus className="inline mr-2" /> Add User
            </button>
          </div>

          {/* User Table */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead className="bg-gray-200">
                <tr>
                  <th className="p-3 text-left cursor-pointer" onClick={() => handleSort("_id")}>
                    User ID {sortConfig.key === "_id" ? (sortConfig.direction === "asc" ? <FaSortUp className="inline" /> : <FaSortDown className="inline" />) : <FaSort className="inline" />}
                  </th>
                  <th className="p-3 text-left cursor-pointer" onClick={() => handleSort("username")}>
                    Username {sortConfig.key === "username" ? (sortConfig.direction === "asc" ? <FaSortUp className="inline" /> : <FaSortDown className="inline" />) : <FaSort className="inline" />}
                  </th>
                  <th className="p-3 text-left cursor-pointer" onClick={() => handleSort("email")}>
                    Email {sortConfig.key === "email" ? (sortConfig.direction === "asc" ? <FaSortUp className="inline" /> : <FaSortDown className="inline" />) : <FaSort className="inline" />}
                  </th>
                  <th className="p-3 text-left">Role</th>
                  <th className="p-3 text-left">Status</th>
                  <th className="p-3 text-left">Permissions</th>
                  <th className="p-3 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedUsers.map((user) => (
                  <tr key={user._id} className="border-b hover:bg-gray-100">
                    <td className="p-3">{user._id}</td>
                    <td className="p-3">
                      {editingUser && editingUser._id === user._id ? (
                        <input type="text" value={editingUser.username} onChange={(e) => setEditingUser({ ...editingUser, username: e.target.value })} />
                      ) : (
                        user.username
                      )}
                    </td>
                    <td className="p-3">
                      {editingUser && editingUser._id === user._id ? (
                        <input type="text" value={editingUser.email} onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })} />
                      ) : (
                        user.email
                      )}
                    </td>
                    <td className="p-3">
                      {editingUser && editingUser._id === user._id ? (
                        <select value={editingUser.isAdmin} onChange={(e) => setEditingUser({ ...editingUser, isAdmin: e.target.value === "true" })}>
                          <option value="false">User</option>
                          <option value="true">Admin</option>
                        </select>
                      ) : user.isAdmin ? (
                        "Admin"
                      ) : (
                        "User"
                      )}
                    </td>
                    <td className="p-3">
                      {editingUser && editingUser._id === user._id ? (
                        <select value={editingUser.enabled} onChange={(e) => setEditingUser({ ...editingUser, enabled: e.target.value === "true" })}>
                          <option value="true">Enabled</option>
                          <option value="false">Disabled</option>
                        </select>
                      ) : user.enabled ? (
                        "Enabled"
                      ) : (
                        "Disabled"
                      )}
                    </td>
                    <td/>
                    <td className="p-3 flex space-x-2">
                      {editingUser && editingUser._id === user._id ? (
                        <button className="text-green-500" onClick={handleSave}>
                          <FaSave />
                        </button>
                      ) : (
                        <button className="text-blue-500" onClick={() => handleEdit(user)}>
                          <FaEdit />
                        </button>
                      )}
                      <button className="text-red-500" onClick={() => handleDelete(user._id)}>
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Add User Modal */}
          {showAddUserModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">      
              <div className="bg-white p-6 rounded-lg shadow-lg w-96">
                <h2 className="text-xl font-bold mb-4">Add New User</h2>        
                {statusMessage && <p className="text-center mt-4 text-red-500 p-2">{statusMessage}</p>}
                <input type="text" placeholder="Username" className="w-full p-2 border border-gray-300 rounded mb-2" value={newUser.username} onChange={(e) => setNewUser({ ...newUser, username: e.target.value })} />
                <input type="email" placeholder="Email" className="w-full p-2 border border-gray-300 rounded mb-2" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} />
                <input type="password" placeholder="Password" className="w-full p-2 border border-gray-300 rounded mb-2" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} />
                <label className="flex items-center">
                  <input type="checkbox" className="mr-2" checked={newUser.isAdmin} onChange={(e) => setNewUser({ ...newUser, isAdmin: e.target.checked })} />
                  Admin
                </label>
                <label className="flex items-center">
                  <input type="checkbox" className="mr-2" checked={newUser.enabled} onChange={(e) => setNewUser({ ...newUser, enabled: e.target.checked })} />
                  Enabled
                </label>
                <div className="mt-4 flex justify-between">
                  <button className="bg-gray-500 text-white px-4 py-2 rounded" onClick={() => {setShowAddUserModal(false); setStatusMessage('')}}>Cancel</button>
                  <button className="bg-green-500 text-white px-4 py-2 rounded" disabled={loading} onClick={handleAddUser}>                    
                    {loading ? "Loading..." : "Add"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex justify-center mt-6 space-x-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-1 bg-gray-300 rounded disabled:opacity-50"
            >
              Previous
            </button>
            {[...Array(totalPages).keys()].map((number) => (
              <button
                key={number + 1}
                onClick={() => handlePageChange(number + 1)}
                className={`px-3 py-1 rounded ${currentPage === number + 1 ? "bg-blue-500 text-white" : "bg-gray-300"}`}
              >
                {number + 1}
              </button>
            ))}
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-1 bg-gray-300 rounded disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUsers;